'use client';

import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { formatUnits } from 'viem';
import { cn } from '@/lib/utils';
import type { SeatData } from '@/components/grid';
import { GamePanel } from '../ui/GamePanel';
import { compareSeatsBoardTimeOrder } from '../board-canvas/tile-layout';
import { formatCompactUsd } from '../data/mock-prices';

/** macOS uses ⌘ for the basket modifier; Windows/Linux uses Ctrl. The
 *  `useLobbyBasket` hook already accepts both keys, but the visual hint
 *  should match the platform the viewer actually has under their hands.
 *  Server-render falls back to the cross-platform `Ctrl` so SSR + first
 *  hydration paint match (no React hydration mismatch warnings — `isMac`
 *  flips inside `useEffect`, after the SSR HTML has shipped). */
function useModifierGlyph(): string {
  const [isMac, setIsMac] = useState(false);
  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const ua = navigator.userAgent || '';
    const platform = navigator.platform || '';
    setIsMac(/Mac|iPhone|iPad|iPod/i.test(platform) || /Mac OS X/i.test(ua));
  }, []);
  return isMac ? '⌘' : 'Ctrl';
}

type SortKey = 'price' | 'runway';

/** Runtime-safe compare — some adapters may stringify bigint in edge cases. */
function seatPrice(v: SeatData['selfAssessedPrice']): bigint {
  if (typeof v === 'bigint') return v;
  try {
    return BigInt(String(v ?? 0));
  } catch {
    return 0n;
  }
}

function compareSeats(a: SeatData, b: SeatData, key: SortKey, dir: 'asc' | 'desc'): number {
  const sign = dir === 'asc' ? 1 : -1;
  if (key === 'price') {
    const pa = seatPrice(a.selfAssessedPrice);
    const pb = seatPrice(b.selfAssessedPrice);
    if (pa !== pb) {
      const cmp = pa < pb ? -1 : 1;
      return sign * cmp;
    }
    return a.seatIndex - b.seatIndex;
  }
  const cmp = compareSeatsBoardTimeOrder(a, b);
  if (cmp !== 0) return sign * cmp;
  return 0;
}

/**
 * Coarse runway on each tile: `45min`, then `1h`…`23h`, `1d`…`6d`, `1w`…, `1m`… (months).
 * Sub-hour uses `min` so `m` stays unambiguous for months.
 */
function formatRunwaySubtitle(seconds: number): string {
  if (seconds <= 0) return '—';
  const M = 60;
  const H = 3600;
  const D = 86400;
  const W = 7 * D;
  const MO = 30 * D;
  if (seconds < H) {
    const mins = Math.max(1, Math.round(seconds / M));
    return `${mins}min`;
  }
  if (seconds < D) {
    return `${Math.round(seconds / H)}h`;
  }
  if (seconds < W) {
    return `${Math.round(seconds / D)}d`;
  }
  if (seconds < MO) {
    return `${Math.max(1, Math.round(seconds / W))}w`;
  }
  return `${Math.max(1, Math.round(seconds / MO))}m`;
}

/** Tax tokens we have a live USD oracle for. Mirrors the same set used by
 *  the canvas overlays (see `BoardCanvas2D.formatSeatPriceLabel`) so a tile
 *  on the board, a pill in My Tiles, and a price chip in the basket all
 *  agree on whether to defer rendering until USD lands. */
const CONVERTIBLE_TAX_TOKEN_SYMBOLS = new Set(['TAKEOVER', 'WETH', 'ETH']);

interface OwnedTilesBarProps {
  seats: SeatData[];
  /** Decimals of the tax token — live chain data = 18, snapshot = 6. */
  priceDecimals?: number;
  /** Optional USD price of one tax token — pills render as USD when > 0. */
  priceTokenPriceUsd?: number;
  /** Tax-token ticker (e.g. `'TAKEOVER'`, `'USDC'`). When the token is
   *  one of the convertible-to-USD tokens (`TAKEOVER` / `WETH` / `ETH`)
   *  and `priceTokenPriceUsd <= 0`, the cell renders a skeleton instead
   *  of the raw token amount so the panel never flashes a 7-digit
   *  pre-conversion value. Optional so legacy callers without symbol
   *  context (snapshot / dev mocks) keep their current behaviour. */
  priceTokenSymbol?: string;
  /** Fires when a pill is clicked without a modifier. Caller opens the
   *  manage drawer for this tile. */
  onManage?: (seat: SeatData) => void;
  /** Fires when a pill is Cmd/Ctrl-clicked. Caller toggles this tile in
   *  the lobby basket so it shows up highlighted on the main board and
   *  rolls into the bulk-action drawer alongside any other multi-selected
   *  seats. Falling back to `onManage` is intentional: panels mounted in
   *  surfaces without a basket (e.g. read-only previews) still get the
   *  legacy single-tile flow. */
  onToggleBasket?: (seat: SeatData) => void;
  /** Indices currently in the lobby basket. Drives the cell-level
   *  raise + cyan glow that mirrors the main board's multi-select
   *  treatment (`LobbySeatTile2D` / `TileInstanced`) so cross-surface
   *  selection state reads consistently. */
  selectedIndices?: Set<number>;
  /** True while `isBoardDataLoading` is set in `LobbyClient` — renders a
   *  skeleton grid so the panel doesn't flash "No tiles yet" (empty state)
   *  while the new board's seat data is still in flight. Empty state is
   *  only correct once the grid has actually loaded. */
  loading?: boolean;
  className?: string;
}

/**
 * "My Tiles" panel — a 5-column grid of the connected wallet's owned seats
 * on the active board. Replaces the legacy single-row pill strip
 * (`OwnedSeatsBar`-style) which became unusable as soon as a holder owned
 * more than ~6 tiles: the strip would either wrap into multiple visible
 * rows (breaking the floating panel chrome) or scroll horizontally
 * (hiding context behind a tiny scrollbar).
 *
 * Visual rules (TBD-MyTiles-001):
 * - Always 5 columns regardless of viewport so the panel keeps a fixed
 *   width and a portfolio of 5/10/25 seats packs into 1/2/5 visible rows.
 * - Sort controls: by **time remaining** (soonest / longest first) or by
 *   **price** (low→high / high→low), matching the board grid sort affordances.
 * - Critical (<1h) → red, warning (<24h) → amber, otherwise neutral sky
 *   to match `potwars-app/components/seat-grid.tsx`.
 * - Forfeited tiles are excluded — the board itself paints a chains+lock
 *   wrap on those, and the My Tiles cell would be misleading because the
 *   seat can no longer be saved by the holder.
 * - Wraps the grid in `GamePanel` so the chrome (title bar, corner
 *   brackets, gilded border) matches Holders/Rewards/Yield instead of
 *   the floating bar's bespoke styling.
 *
 * The export name (`OwnedTilesBar`) is preserved so consumers in
 * `LobbyClient.tsx` don't need to change. The file is the single source
 * of truth for the My Tiles HUD slot.
 */
export function OwnedTilesBar({
  seats,
  priceDecimals = 6,
  priceTokenPriceUsd = 1,
  priceTokenSymbol,
  onManage,
  onToggleBasket,
  selectedIndices,
  loading = false,
  className,
}: OwnedTilesBarProps) {
  const [sortKey, setSortKey] = useState<SortKey>('runway');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const modGlyph = useModifierGlyph();

  const owned = useMemo(() => {
    const list = seats.filter(
      (s) => s.isOwn && s.holder && !s.isForfeited && s.runwaySeconds > 0,
    );
    return [...list].sort((a, b) => compareSeats(a, b, sortKey, sortDir));
  }, [seats, sortKey, sortDir]);

  const setSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'price' ? 'desc' : 'asc');
    }
  };

  return (
    <GamePanel
      title="My Tiles"
      tone="default"
      className={cn('w-[260px]', className)}
      bodyClassName="p-2"
      trailing={
        <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-amber-200/70 tabular-nums">
          {loading ? '…' : owned.length}
        </span>
      }
    >
      {loading ? (
        <div
          className="grid grid-cols-5 gap-1.5"
          aria-busy="true"
          aria-live="polite"
        >
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className="aspect-square w-full rounded-[4px] border border-white/5 bg-white/[0.04] animate-pulse"
            />
          ))}
        </div>
      ) : owned.length === 0 ? (
        // Keep the panel visible when toggled on but the connected wallet
        // (or guest visitor) doesn't own any seats on this board, so the
        // HUD chrome doesn't appear "broken" when the user clicks the
        // pill. The empty state mirrors the BoardroomCard CTA copy.
        <div className="px-1 py-2 text-center">
          <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 leading-relaxed">
            No tiles yet
          </p>
          <p className="mt-1 text-[10px] text-zinc-600 leading-snug">
            Claim a seat on the board to see it here.
          </p>
        </div>
      ) : (
        <div className="-mx-2 px-2 space-y-2">
          <div className="flex items-center gap-1 px-0.5 pb-0.5 border-b border-amber-500/10">
            <span className="text-[8px] font-mono uppercase tracking-[0.12em] text-zinc-500 shrink-0">
              Sort
            </span>
            <div className="grid grid-cols-2 flex-1 gap-0.5 min-w-0">
              <button
                type="button"
                onClick={() => setSort('runway')}
                className={cn(
                  'min-w-0 px-0.5 py-0.5 rounded-[3px] text-[7px] font-mono uppercase tracking-[0.06em] transition-colors leading-tight',
                  sortKey === 'runway'
                    ? 'bg-amber-500/20 text-amber-100 border border-amber-400/40'
                    : 'text-zinc-500 border border-transparent hover:bg-white/5 hover:text-zinc-300',
                )}
                title={
                  sortKey === 'runway'
                    ? sortDir === 'asc'
                      ? 'Runway: soonest first — click for longest first'
                      : 'Runway: longest first — click for soonest first'
                    : 'Sort by runway (time left)'
                }
              >
                Runway{' '}
                <span className="tabular-nums opacity-80">
                  {sortKey === 'runway' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSort('price')}
                className={cn(
                  'min-w-0 px-0.5 py-0.5 rounded-[3px] text-[7px] font-mono uppercase tracking-[0.06em] transition-colors leading-tight',
                  sortKey === 'price'
                    ? 'bg-amber-500/20 text-amber-100 border border-amber-400/40'
                    : 'text-zinc-500 border border-transparent hover:bg-white/5 hover:text-zinc-300',
                )}
                title={
                  sortKey === 'price'
                    ? sortDir === 'desc'
                      ? 'Price: high → low — click for low → high'
                      : 'Price: low → high — click for high → low'
                    : 'Sort by price'
                }
              >
                Price{' '}
                <span className="tabular-nums opacity-80">
                  {sortKey === 'price' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                </span>
              </button>
            </div>
          </div>
          <div
            data-gild-scroll
            className="max-h-[200px] overflow-y-auto pb-1"
          >
          <div className="grid grid-cols-5 gap-1.5">
            {owned.map((s) => (
              <OwnedTileCell
                key={s.seatIndex}
                seat={s}
                priceDecimals={priceDecimals}
                priceTokenPriceUsd={priceTokenPriceUsd}
                priceTokenSymbol={priceTokenSymbol}
                isSelected={selectedIndices?.has(s.seatIndex) ?? false}
                onSelect={(seat, opts) => {
                  if (opts.toggleBasket && onToggleBasket) {
                    onToggleBasket(seat);
                    return;
                  }
                  onManage?.(seat);
                }}
              />
            ))}
          </div>
          </div>
          {/* Multi-select hint — sits *outside* the scroll container so
           *  it stays anchored to the bottom of the panel even on tall
           *  portfolios. Keeps cross-surface parity with the on-canvas
           *  basket flow ({@link useLobbyBasket}) where Cmd/Ctrl+click is
           *  the discoverability gap users hit most often. */}
          {onToggleBasket && (
            <div className="flex items-center justify-center gap-1.5 pt-1.5 mt-1 border-t border-amber-500/10 text-[8.5px] font-mono uppercase tracking-[0.16em] text-zinc-500 leading-none">
              <kbd className="px-1 py-px rounded-[3px] border border-amber-400/35 bg-black/55 text-amber-100/90 text-[8.5px] font-mono leading-none shadow-[inset_0_-1px_0_rgba(0,0,0,0.4)]">
                {modGlyph}
              </kbd>
              <span>+ click to multi-select</span>
            </div>
          )}
        </div>
      )}
    </GamePanel>
  );
}

/**
 * Horizontal strip variant of "My Tiles" for mobile. Renders the same
 * owned-seat pills as the desktop `OwnedTilesBar` panel but laid out in
 * a single row that scrolls horizontally — no overlay, no sort chrome,
 * no `GamePanel` wrapper. Desktop's default sort (`runway` asc — soonest
 * runway first) is used verbatim so the ordering is consistent with what
 * users see on a desktop session; the user-visible sort toggle lives on
 * the desktop panel only, which matches the user's ask ("same sorting
 * logic as desktop").
 *
 * Mount point: rendered above the board canvas below the chip rail when
 * the mobile user taps the "My Tiles" chip. The board stays visible
 * underneath (a sheet / overlay was explicitly rejected).
 */
export function OwnedTilesStrip({
  seats,
  priceDecimals = 6,
  priceTokenPriceUsd = 1,
  priceTokenSymbol,
  onManage,
  onToggleBasket,
  selectedIndices,
  loading = false,
  className,
}: Omit<OwnedTilesBarProps, 'className'> & { className?: string }) {
  const owned = useMemo(() => {
    const list = seats.filter(
      (s) => s.isOwn && s.holder && !s.isForfeited && s.runwaySeconds > 0,
    );
    // Match desktop's default: soonest runway first. Bare sort — no UI
    // toggle — because the strip is space-constrained and the user's
    // existing desktop preference isn't plumbed to mobile (we don't
    // persist sort across form factors yet).
    return [...list].sort((a, b) => compareSeats(a, b, 'runway', 'asc'));
  }, [seats]);

  if (loading) {
    return (
      <div
        className={cn(
          'w-full overflow-x-auto scrollbar-none bg-black/70 backdrop-blur-sm',
          'border-b border-amber-500/20',
          className,
        )}
        aria-busy="true"
        aria-live="polite"
      >
        <div className="flex items-center gap-1.5 px-2 py-1.5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-10 w-14 shrink-0 rounded-[4px] border border-white/5 bg-white/[0.04] animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-full overflow-x-auto scrollbar-none bg-black/70 backdrop-blur-sm',
        'border-b border-amber-500/20',
        className,
      )}
      role="region"
      aria-label="My Tiles"
    >
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        <span className="shrink-0 text-[9px] font-mono uppercase tracking-[0.2em] text-amber-200/70">
          My&nbsp;Tiles
          <span className="ml-1 tabular-nums text-zinc-400">{owned.length}</span>
        </span>
        {owned.length === 0 ? (
          // Placeholder copy matches the top-level `MobileBoardRail`
          // empty state ("Joined boards will show here"): both surfaces
          // describe the *intent* of the strip — this is where your
          // tiles will land when you claim one — instead of using the
          // neutral "No tiles yet" which read as a failure state on a
          // fresh mobile visit.
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500 px-1">
            Your tiles will show here
          </span>
        ) : (
          owned.map((s) => (
            // `h-12 w-12` keeps `aspect-square` on the cell itself (the
            // existing `OwnedTileCell` styles), so pills in the strip
            // look identical to the desktop panel's cells and the user
            // sees the same price + runway glyphs they already know.
            // No desktop copy changes; this is purely mobile plumbing.
            <div key={s.seatIndex} className="h-12 w-12 shrink-0">
              <OwnedTileCell
                seat={s}
                priceDecimals={priceDecimals}
                priceTokenPriceUsd={priceTokenPriceUsd}
                priceTokenSymbol={priceTokenSymbol}
                isSelected={selectedIndices?.has(s.seatIndex) ?? false}
                onSelect={(seat, opts) => {
                  if (opts.toggleBasket && onToggleBasket) {
                    onToggleBasket(seat);
                    return;
                  }
                  onManage?.(seat);
                }}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function OwnedTileCell({
  seat,
  priceDecimals,
  priceTokenPriceUsd,
  priceTokenSymbol,
  isSelected = false,
  onSelect,
}: {
  seat: SeatData;
  priceDecimals: number;
  priceTokenPriceUsd: number;
  priceTokenSymbol?: string;
  isSelected?: boolean;
  /** Click handler — `opts.toggleBasket` is true when the user held
   *  Cmd (macOS) or Ctrl (Win/Linux), letting the parent route the
   *  click into the lobby basket instead of the manage drawer. The
   *  modifier check lives here so both the panel grid and the mobile
   *  strip share one source of truth. */
  onSelect: (seat: SeatData, opts: { toggleBasket: boolean }) => void;
}) {
  // Urgency thresholds mirror potwars-app (<1h critical, <24h warning).
  const critical = seat.runwaySeconds > 0 && seat.runwaySeconds < 3600;
  const warning = !critical && seat.runwaySeconds > 0 && seat.runwaySeconds < 86400;
  const urgent = critical || warning;

  /** True while the cell is waiting on a USD price for a convertible
   *  tax token (TAKEOVER / WETH / ETH). Drives a skeleton bar in place
   *  of the price text so users never see the raw 18-dp token amount
   *  flash before the oracle resolves. */
  const isPricePending =
    priceTokenPriceUsd <= 0 &&
    !!priceTokenSymbol &&
    CONVERTIBLE_TAX_TOKEN_SYMBOLS.has(priceTokenSymbol);

  const priceLabel = useMemo(() => {
    if (isPricePending) return null;
    const raw = seat.selfAssessedPrice;
    if (!raw || raw === 0n) return '—';
    const tokens = Number(formatUnits(raw, priceDecimals));
    if (!Number.isFinite(tokens) || tokens <= 0) return '—';
    if (priceTokenPriceUsd > 0) {
      return formatCompactUsd(tokens * priceTokenPriceUsd);
    }
    if (tokens >= 1000) return `${(tokens / 1000).toFixed(2)}K`;
    if (tokens >= 100) return `${Math.round(tokens)}`;
    if (tokens >= 10) return tokens.toFixed(1);
    return tokens.toFixed(2);
  }, [seat.selfAssessedPrice, priceDecimals, priceTokenPriceUsd, isPricePending]);

  const runwayLabel = formatRunwaySubtitle(seat.runwaySeconds);
  // The accessibility title falls back to the runway-only string while
  // the USD price is pending so screen readers / hover text don't
  // announce a missing dollar value.
  const titlePriceFragment = isPricePending ? 'price loading' : priceLabel;
  const title = isSelected
    ? `${runwayLabel} left · ${titlePriceFragment} · In basket — click to remove, plain click to manage`
    : urgent
      ? `Expires in ${formatDurationDetail(seat.runwaySeconds)} · ${titlePriceFragment}`
      : `${runwayLabel} left · ${titlePriceFragment} · Manage · ⌘/Ctrl-click to multi-select`;

  return (
    <button
      type="button"
      onClick={(e: MouseEvent<HTMLButtonElement>) => {
        // Forward the modifier state so the parent can split into
        // basket-toggle vs. manage-drawer paths. Matches the canvas's
        // `{ toggleBasket }` contract (`onTileClick` in
        // `BoardCanvas2D` / `BoardCanvas3D`) so both surfaces share
        // one mental model: hold Cmd/Ctrl → it goes in the basket.
        onSelect(seat, { toggleBasket: e.metaKey || e.ctrlKey });
      }}
      title={title}
      aria-label={title}
      aria-pressed={isSelected || undefined}
      className={cn(
        'group relative aspect-square w-full',
        'flex flex-col items-center justify-center gap-0.5',
        // `transition-all` (was `transition-colors`) lets the cyan
        // raise + glow tween smoothly when the cell flips into / out
        // of the basket — otherwise the scale jumps in one frame.
        'rounded-[4px] border transition-all cursor-pointer',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60',
        critical
          ? 'border-red-500/60 text-red-100 bg-red-500/15 hover:bg-red-500/25'
          : warning
            ? 'border-amber-500/50 text-amber-100 bg-amber-500/10 hover:bg-amber-500/20'
            : 'border-sky-500/30 text-sky-100 bg-sky-500/10 hover:bg-sky-500/20',
        // Multi-select treatment — same cobalt-cyan vocabulary the
        // 2D / 3D board uses for basket'd seats (see
        // `LobbySeatTile2D.tsx` and `TileInstanced.tsx`). Stacks
        // *over* the urgency colour so a critical tile in the basket
        // still reads as in-basket first; the red rim stays visible
        // through the cyan ring's offset. `scale-[1.08]` matches the
        // hover-lift-equivalent on the on-canvas tile (`scale-110`)
        // scaled down a hair so a 5-column grid of basketed tiles
        // doesn't crash into its neighbours.
        isSelected && [
          'z-10 scale-[1.08]',
          'ring-[2px] ring-cyan-300 ring-offset-1 ring-offset-black/60',
          'shadow-[0_0_0_1px_rgba(207,250,254,0.95),0_0_14px_3px_rgba(34,211,238,0.55)]',
          // Lift the body brightness to the hover-bg level so the
          // cell reads as "raised" the way a hover does — basket
          // selection without it looked like a glowing-but-dim cell
          // sitting under the rest of the grid.
          critical
            ? 'bg-red-500/25'
            : warning
              ? 'bg-amber-500/20'
              : 'bg-sky-500/20',
        ],
      )}
    >
      {urgent && !isSelected && (
        <AlertTriangle
          className={cn(
            'absolute top-0.5 right-0.5 h-2.5 w-2.5',
            critical ? 'text-red-300' : 'text-amber-300',
          )}
        />
      )}
      {isSelected && (
        <span
          aria-hidden
          className="absolute top-0 right-0 z-30 bg-cyan-400 rounded-bl-[4px] rounded-tr-[3px] p-px shadow-[0_0_6px_rgba(34,211,238,0.8)]"
        >
          <Check className="h-2.5 w-2.5 text-black" strokeWidth={3} />
        </span>
      )}
      {isPricePending ? (
        <span
          aria-busy="true"
          aria-label="Price loading"
          className="block h-2 w-7 rounded-[2px] bg-amber-500/20 animate-pulse"
        />
      ) : (
        <span className="text-[10.5px] font-mono font-semibold tabular-nums leading-none">
          {priceLabel}
        </span>
      )}
      <span className="text-[8px] font-mono tracking-[0.08em] tabular-nums opacity-60 leading-none">
        {runwayLabel}
      </span>
    </button>
  );
}

/** Tooltip / title — more precise than the coarse subtitle. */
function formatDurationDetail(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}min`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  if (seconds < 7 * 86400) return `${Math.round(seconds / 86400)}d`;
  if (seconds < 30 * 86400) return `${Math.round(seconds / (7 * 86400))}w`;
  return `${Math.round(seconds / (30 * 86400))}m`;
}
